import {
  rrcProfileId,
  rrcProfileKey,
  type RrcProfileId,
  type RrcProfileKey,
} from '../rrc-profile-query';
import { otsBaseApi } from '../../ots-api';
import { RrcLocationProfile } from '../../../model/rrc/rrc-location-profile';

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: [
    'RrcLocationProfiles',
    'ObjectRrcLocationProfiles',
    'KeyRrcLocationProfiles',
    'CurrentRrcLocationProfile',
    'ObjectObjectChangeLogs',
  ],
});

export const rrcLocationProfileApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentRrcLocationProfilesByLocation: builder.query<
      RrcLocationProfile[],
      { locationId: string; locationType: string }
    >({
      query: (arg) => ({
        url: '/compliance/rrc/locations/current',
        params: { locationId: arg.locationId, locationType: arg.locationType },
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'RrcLocationProfiles', id: 'LIST' },
        { type: 'ObjectRrcLocationProfiles', id: arg.locationId },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcLocationProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcLocationProfilesByLocation: builder.query<
      RrcLocationProfile[],
      { locationId: string; locationType: string }
    >({
      query: (arg) => ({
        url: '/compliance/rrc/locations',
        params: { locationId: arg.locationId, locationType: arg.locationType },
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'RrcLocationProfiles', id: 'LIST' },
        { type: 'ObjectRrcLocationProfiles', id: arg.locationId },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcLocationProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcLocationProfilesByKey: builder.query<
      RrcLocationProfile[],
      RrcProfileKey
    >({
      query: (arg) => ({
        url: `/compliance/rrc/locations/by-key/${encodeURIComponent(
          rrcProfileKey(arg).profileKey
        )}/versions`,
        params: { tenantCode: rrcProfileKey(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileKey(queryArgs),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'KeyRrcLocationProfiles', id: rrcProfileKey(arg).profileKey },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcLocationProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcLocationProfile: builder.query<RrcLocationProfile, RrcProfileId>({
      query: (arg) => ({
        url: `/compliance/rrc/locations/${encodeURIComponent(
          rrcProfileId(arg).id
        )}`,
        params: { tenantCode: rrcProfileId(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileId(queryArgs),
      providesTags: (result, error, arg) => [
        { type: 'RrcLocationProfiles', id: rrcProfileId(arg).id },
      ],
    }),
    getCurrentRrcLocationProfileByKey: builder.query<
      RrcLocationProfile,
      RrcProfileKey
    >({
      query: (arg) => ({
        url: `/compliance/rrc/locations/by-key/${encodeURIComponent(
          rrcProfileKey(arg).profileKey
        )}`,
        params: { tenantCode: rrcProfileKey(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileKey(queryArgs),
      providesTags: (result, error, arg) => [
        {
          type: 'CurrentRrcLocationProfile',
          id: rrcProfileKey(arg).profileKey,
        },
        { type: 'KeyRrcLocationProfiles', id: rrcProfileKey(arg).profileKey },
        ...(result
          ? [{ type: 'RrcLocationProfiles' as const, id: result.id }]
          : []),
      ],
    }),
    createRrcLocationProfile: builder.mutation<
      RrcLocationProfile,
      Partial<RrcLocationProfile>
    >({
      query: (arg) => ({
        url: '/compliance/rrc/locations',
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'RrcLocationProfiles', id: 'LIST' },
            { type: 'KeyRrcLocationProfiles', id: result.profileKey },
            { type: 'ObjectRrcLocationProfiles', id: result.locationId },
            { type: 'CurrentRrcLocationProfile' },
          ];
        } else {
          return [
            { type: 'RrcLocationProfiles', id: 'LIST' },
            { type: 'KeyRrcLocationProfiles' },
            { type: 'ObjectRrcLocationProfiles' },
            { type: 'CurrentRrcLocationProfile' },
          ];
        }
      },
    }),
    updateRrcLocationProfile: builder.mutation<
      RrcLocationProfile,
      Partial<RrcLocationProfile>
    >({
      query: (arg) => ({
        url: '/compliance/rrc/locations',
        method: 'PUT',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'RrcLocationProfiles', id: 'LIST' },
            { type: 'KeyRrcLocationProfiles', id: result.profileKey },
            { type: 'ObjectRrcLocationProfiles', id: result.locationId },
            { type: 'CurrentRrcLocationProfile' },
          ];
        } else {
          return [
            { type: 'RrcLocationProfiles', id: 'LIST' },
            { type: 'KeyRrcLocationProfiles' },
            { type: 'ObjectRrcLocationProfiles' },
            { type: 'CurrentRrcLocationProfile' },
          ];
        }
      },
    }),
    deleteRrcLocationProfile: builder.mutation<
      void,
      Partial<RrcLocationProfile>
    >({
      query: (arg) => ({
        url: '/compliance/rrc/locations',
        method: 'DELETE',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        return [
          { type: 'RrcLocationProfiles', id: 'LIST' },
          { type: 'KeyRrcLocationProfiles' },
          { type: 'ObjectRrcLocationProfiles' },
          { type: 'CurrentRrcLocationProfile' },
        ];
      },
    }),
  }),
});

export const {
  useGetCurrentRrcLocationProfilesByLocationQuery,
  useGetRrcLocationProfilesByLocationQuery,
  useGetRrcLocationProfilesByKeyQuery,
  useGetRrcLocationProfileQuery,
  useGetCurrentRrcLocationProfileByKeyQuery,
  useCreateRrcLocationProfileMutation,
  useUpdateRrcLocationProfileMutation,
  useDeleteRrcLocationProfileMutation,
} = rrcLocationProfileApi;
